interface WatermarkConfig {
  text: string;
  username: string;
  opacity: number;
  fontSize: number;
  rotation: number;
}

class WatermarkService {
  private config: WatermarkConfig | null = null;
  private elements: HTMLElement[] = [];
  private canvas: HTMLCanvasElement | null = null;
  private intervals: NodeJS.Timeout[] = [];
  private observer: MutationObserver | null = null;
  private isActive = false;

  public initialize(username: string): void {
    this.config = {
      text: username,
      username,
      opacity: 0.10,
      fontSize: 64,
      rotation: -45,
    };
  }

  public start(): void {
    if (!this.config || this.isActive) return;
    
    this.isActive = true;
    this.createLayers();
    this.setupProtection();
  }

  public stop(): void {
    this.isActive = false;
    
    this.intervals.forEach(interval => clearInterval(interval));
    this.intervals = [];
    
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    this.elements.forEach(el => el.remove());
    this.elements = [];
    
    if (this.canvas) {
      this.canvas.remove();
      this.canvas = null;
    }
    
    this.removeEventListeners();
  }

  private createLayers(): void {
    this.createDOMWatermark();    
    this.createCanvasWatermark();
    this.createShadowWatermark();
    
  }

  private createDOMWatermark(): void {
    const watermark = document.createElement('div');
    watermark.textContent = this.config!.text;
    watermark.setAttribute('data-watermark', 'true');
    
    const responsiveFontSize = Math.min(this.config!.fontSize, window.innerWidth / 20);
    
    Object.assign(watermark.style, {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: `translate(-50%, -50%) rotate(${this.config!.rotation}deg)`,
      fontSize: `${responsiveFontSize}px`,
      fontWeight: '300',
      color: 'rgba(128, 128, 128, 0.3)',
      pointerEvents: 'none',
      userSelect: 'none',
      zIndex: '9999',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      whiteSpace: 'nowrap',
      maxWidth: '90vw',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });

    document.body.appendChild(watermark);
    this.elements.push(watermark);
  }

  private createCanvasWatermark(): void {
    this.canvas = document.createElement('canvas');
    
    Object.assign(this.canvas.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      pointerEvents: 'none',
      zIndex: '9998',
    });

    this.drawCanvas();
    document.body.appendChild(this.canvas);
    window.addEventListener('resize', this.drawCanvas);
  }

  private drawCanvas = (): void => {
    if (!this.canvas || !this.config) return;
    
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    
    const baseFontSize = Math.min(this.config.fontSize - 6, this.canvas.width / 15);
    ctx.font = `300 ${baseFontSize}px Arial, sans-serif`;
    ctx.fillStyle = 'rgba(128, 128, 128, 0.25)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const textWidth = ctx.measureText(this.config.username).width;
    const maxWidth = this.canvas.width * 0.8;
    
    if (textWidth > maxWidth) {
      const scale = maxWidth / textWidth;
      ctx.font = `300 ${baseFontSize * scale}px Arial, sans-serif`;
    }
    
    ctx.save();
    ctx.translate(this.canvas.width * 0.5, this.canvas.height * 0.3);
    ctx.rotate((this.config.rotation * Math.PI) / 180);
    ctx.fillText(this.config.username, 0, 0);
    ctx.restore();
      };

  private createShadowWatermark(): void {
    const container = document.createElement('div');
    
    Object.assign(container.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      pointerEvents: 'none',
      zIndex: '9997',
    });
    
    const shadow = container.attachShadow({ mode: 'closed' });
    
    const watermark = document.createElement('div');
    watermark.textContent = this.config!.username;
    
    const shadowFontSize = Math.min(this.config!.fontSize - 10, window.innerWidth / 20);
    
    Object.assign(watermark.style, {
      position: 'absolute',
      top: '70%',
      left: '50%',
      transform: `translate(-50%, -50%) rotate(${this.config!.rotation}deg)`,
      fontSize: `${shadowFontSize}px`,
      fontWeight: '300',
      color: 'rgba(128, 128, 128, 0.25)',
      pointerEvents: 'none',
      userSelect: 'none',
      fontFamily: 'Arial, sans-serif',
      whiteSpace: 'nowrap',
      maxWidth: '90vw',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    
    shadow.appendChild(watermark);
    document.body.appendChild(container);
    this.elements.push(container);
  }

  private setupProtection(): void {
    this.setupMutationObserver();
    
    const checkInterval = setInterval(() => {
      if (this.isActive) this.checkIntegrity();
    }, 3000);
    this.intervals.push(checkInterval);
    
    this.setupEventListeners();
  }

  private setupMutationObserver(): void {
    this.observer = new MutationObserver((mutations) => {
      if (!this.isActive) return;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
          const removed = Array.from(mutation.removedNodes);
          const watermarkRemoved = removed.some(node => 
            this.elements.includes(node as HTMLElement)
          );
          
          if (watermarkRemoved) {
            setTimeout(() => this.heal(), 100);
          }
        }
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private checkIntegrity(): void {
    const existingElements = this.elements.filter(el => 
      document.body.contains(el)
    );
    
    if (existingElements.length < this.elements.length) {
      this.heal();
    }
  }

  private heal(): void {
    if (!this.isActive || !this.config) return;
    
    this.elements.forEach(el => {
      if (!document.body.contains(el)) {
        const index = this.elements.indexOf(el);
        if (index > -1) this.elements.splice(index, 1);
      }
    });
    
    if (this.elements.length < 2) {
      this.createDOMWatermark();
    }
    
    if (!this.canvas || !document.body.contains(this.canvas)) {
      this.createCanvasWatermark();
    }
  }

  private setupEventListeners(): void {
    document.addEventListener('contextmenu', this.preventContextMenu);
    document.addEventListener('selectstart', this.preventSelection);
  }

  private removeEventListeners(): void {
    document.removeEventListener('contextmenu', this.preventContextMenu);
    document.removeEventListener('selectstart', this.preventSelection);
    window.removeEventListener('resize', this.drawCanvas);
  }

  private preventContextMenu = (e: MouseEvent): void => {
    const target = e.target as Element;
    if (target?.hasAttribute?.('data-watermark')) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  private preventSelection = (e: Event): void => {
    const target = e.target as Element;
    if (target?.hasAttribute?.('data-watermark')) {
      e.preventDefault();
    }
  };

  public getStatus(): { active: boolean; elementsCount: number } {
    return {
      active: this.isActive,
      elementsCount: this.elements.length + (this.canvas ? 1 : 0),
    };
  }
}

// Watermark Protection Service
// Three-layer protection: DOM (center), Canvas (top), Shadow DOM (bottom)
// Auto-healing via MutationObserver detects and recreates removed watermarks
// All layers display username with responsive sizing to fit within viewport
export default new WatermarkService();

